import { useContext, useMemo } from 'react';
import AvailableWorkSpacesContext from 'contexts/AvailableWorkSpacesContext';
import { LabelsContext } from 'contextProviders/SplashScreenProviders/ContentProviders/LabelsProvider';
import { UIVersionContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import { V3AppsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import isContentItemMatch from 'screens/V3/ContentIndex/isContentItemMatch';
import useContent from 'screens/V3/ContentIndex/useContent';
import GLOBAL_SEARCH from '../constants';
import useContentLink from './useContentLink';

const isNameMatch = ({
  name,
  searchText,
}: {
  name: string;
  searchText: string;
}) => name.toLowerCase().includes(searchText.toLowerCase());

// Names starting with the search text first, then alphabetical
const sortResults = ({
  results,
  searchText,
}: {
  results: GlobalSearch.ContentResult[];
  searchText: string;
}) => {
  const lowerSearchText = searchText.toLowerCase();
  return [...results].sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    const aStarts = aName.startsWith(lowerSearchText);
    const bStarts = bName.startsWith(lowerSearchText);
    if (aStarts !== bStarts) {
      return aStarts ? -1 : 1;
    }
    return aName.localeCompare(bName);
  });
};

const useContentResults = ({ searchText }: { searchText: string }) => {
  const { isV3 } = useContext(UIVersionContext);
  const { labelLookup } = useContext(LabelsContext);
  const { availableWorkspaces, availableProducts } = useContext(V3AppsContext);
  const { availableWorkSpaces } = useContext(AvailableWorkSpacesContext);
  const contentItems = useContent();
  const {
    buildContentItemLink,
    buildLegacyWorkSpaceLink,
    buildV3WorkspaceLink,
    buildV3ProductLink,
  } = useContentLink();

  return useMemo((): GlobalSearch.ContentResultGroup[] => {
    const trimmedSearchText = searchText.trim();
    if (trimmedSearchText === '') {
      return [];
    }

    const contentResults = contentItems
      .filter(
        (contentItem) =>
          !contentItem.isHidden &&
          isContentItemMatch({
            contentItem,
            searchText: trimmedSearchText,
            labelLookup,
          }),
      )
      .map(
        (contentItem): GlobalSearch.ContentResult => ({
          type: contentItem.type,
          id: contentItem.key,
          name: contentItem.name,
          link: buildContentItemLink(contentItem),
        }),
      );

    const workspaceResults: GlobalSearch.ContentResult[] = isV3
      ? [
          ...availableWorkspaces
            .filter((w) => !w.isHidden)
            .map(
              (w): GlobalSearch.ContentResult => ({
                type: 'workspace',
                id: w.id,
                name: w.label,
                link: buildV3WorkspaceLink(w),
              }),
            ),
          ...availableProducts
            .filter((p) => !p.isHidden)
            .map(
              (p): GlobalSearch.ContentResult => ({
                type: 'app',
                id: p.id,
                name: p.label,
                link: buildV3ProductLink(p),
              }),
            ),
        ]
      : availableWorkSpaces
          .filter((w) => !('isHidden' in w && w.isHidden))
          .map(
            (w): GlobalSearch.ContentResult => ({
              type: 'workspace',
              id: w.id,
              name: w.title,
              link: buildLegacyWorkSpaceLink(w),
            }),
          );

    const results = [
      ...contentResults,
      ...workspaceResults.filter((r) =>
        isNameMatch({ name: r.name, searchText: trimmedSearchText }),
      ),
    ];

    return GLOBAL_SEARCH.CONTENT_RESULT_GROUPS.map(
      (group): GlobalSearch.ContentResultGroup => ({
        ...group,
        results: sortResults({
          results: results.filter((r) => r.type === group.type),
          searchText: trimmedSearchText,
        }),
      }),
    ).filter((group) => group.results.length > 0);
  }, [
    availableProducts,
    availableWorkSpaces,
    availableWorkspaces,
    buildContentItemLink,
    buildLegacyWorkSpaceLink,
    buildV3ProductLink,
    buildV3WorkspaceLink,
    contentItems,
    isV3,
    labelLookup,
    searchText,
  ]);
};

export default useContentResults;
